import React, { FC, useEffect, useState } from 'react';
import { Accordion, AccordionDetails, AccordionSummary, Box, IconButton, List, Typography } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { NShiftPartner, NShiftPartnerService, NShiftPartnerServiceAddon } from 'models/nshift-partners';
import { TransportPartner, TransportPartnerService } from 'models/organization';
import NShiftPartnerServicesAutoComplete from './nshift-partner-services-auto-complete.component';
import NShiftPartnerServiceItem from './nshift-partner-service-item/nshift-partner-service-item.component';

type NShiftPartnerAccordionProps = {
    selectedPartner: NShiftPartner;
    onPartnerDelete: (index: number) => void;
    index: number;
    onServiceChange: (partner: NShiftPartner, services: TransportPartnerService[]) => void;
    savedPartner?: TransportPartner;
};
const NShiftPartnerAccordion: FC<NShiftPartnerAccordionProps> = ({
    selectedPartner,
    onPartnerDelete,
    index,
    onServiceChange,
    savedPartner,
}: NShiftPartnerAccordionProps) => {
    const [expanded, setExpanded] = useState<boolean>(true);
    const [services, setServices] = useState<NShiftPartnerService[]>([]);
    const [selectedPartnerServices, setSelectedPartnerServices] = useState<NShiftPartnerService[]>([]);
    const [transportPartnerServiceToSave, setTransportPartnerServiceToSave] = useState<TransportPartnerService[]>([]);

    const toggleAccordion = () => {
        setExpanded(!expanded);
    };

    useEffect(() => {
        if (selectedPartner) {
            const servicesForDropdown = [...selectedPartner.services];
            if (savedPartner?.services?.length) {
                const selectedServices: NShiftPartnerService[] = [];
                for (const service of savedPartner.services) {
                    const index = servicesForDropdown.findIndex(
                        (serviceForDropdown) => serviceForDropdown.id === service.id,
                    );
                    if (index > -1) {
                        selectedServices.push(servicesForDropdown[index]);
                        servicesForDropdown.splice(index, 1);
                    }
                }
                setSelectedPartnerServices(selectedServices);
                setTransportPartnerServiceToSave(savedPartner?.services);
            }
            setServices(servicesForDropdown);
        }
    }, [savedPartner, selectedPartner]);

    const handleAddonSelect = (service: NShiftPartnerService, addons: NShiftPartnerServiceAddon[]) => {
        const transportPartnerServices = [...transportPartnerServiceToSave];
        const serviceIndex = transportPartnerServices.findIndex((transportPartnerService) => {
            return transportPartnerService?.id === service?.id;
        });
        let paymentMethodType = '';
        if (selectedPartner.id === 'PBREV' && service.id === 'BEXPD') {
            paymentMethodType = 'metered';
        }
        const data = {
            id: service.id,
            description: service.description,
            addons: addons.map((addon) => addon.id),
            paymentMethodType: paymentMethodType,
        };
        if (serviceIndex > -1) {
            transportPartnerServices.splice(serviceIndex, 1, data);
        } else {
            transportPartnerServices.push(data);
        }
        setTransportPartnerServiceToSave(transportPartnerServices);
        onServiceChange(selectedPartner, transportPartnerServices);
    };

    const handleServiceSelect = (service: NShiftPartnerService) => {
        const selectedServices = [...selectedPartnerServices];
        selectedServices.push(service);
        setSelectedPartnerServices(selectedServices);

        const partnerServices = [...services];
        const partnerServiceIndex = partnerServices.findIndex((nShiftPartnerService) => {
            return nShiftPartnerService?.id === service?.id;
        });
        if (partnerServiceIndex > -1) {
            partnerServices.splice(partnerServiceIndex, 1);
            setServices(partnerServices);
        }
        handleAddonSelect(service, []);
    };

    const handleServiceDelete = (index: number) => {
        const service = selectedPartnerServices[index];

        const selectedServices = [...selectedPartnerServices];
        selectedServices.splice(index, 1);
        setSelectedPartnerServices(selectedServices);

        const transportPartnerServices = [...transportPartnerServiceToSave];
        transportPartnerServices.splice(index, 1);
        setTransportPartnerServiceToSave(transportPartnerServices);

        const partnerServices = [...services];
        partnerServices.push(service);
        setServices(partnerServices);
        onServiceChange(selectedPartner, transportPartnerServices);
    };

    return (
        <Box>
            <Accordion sx={{ border: '1px solid #ddd' }} elevation={0} expanded={expanded} onChange={toggleAccordion}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />} aria-controls="panel1-content" id="panel1-header">
                    <Box display="flex" alignItems="center">
                        <Typography>
                            {selectedPartner.description} ({selectedPartner.id})
                        </Typography>
                        <IconButton onClick={() => onPartnerDelete(index)} aria-label="delete" size="small">
                            <DeleteIcon />
                        </IconButton>
                    </Box>
                </AccordionSummary>
                <AccordionDetails>
                    <Box>
                        <NShiftPartnerServicesAutoComplete
                            nShiftPartnerServices={services}
                            onServiceSelect={handleServiceSelect}
                        />
                    </Box>
                    {selectedPartnerServices.length ? (
                        <List>
                            {selectedPartnerServices.map((selectedPartnerService, index) => {
                                return (
                                    <NShiftPartnerServiceItem
                                        key={index}
                                        index={index}
                                        selectedPartnerService={selectedPartnerService}
                                        onAddonSelect={handleAddonSelect}
                                        onServiceDelete={handleServiceDelete}
                                        savedService={transportPartnerServiceToSave[index]}
                                    />
                                );
                            })}
                        </List>
                    ) : undefined}
                </AccordionDetails>
            </Accordion>
        </Box>
    );
};

export default NShiftPartnerAccordion;
